import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const LIST_EASE = [0.32, 0.72, 0, 1];

// Per-digit blur+slide flip — used for stat tiles, earnings, etc.
const DIGIT_SPRING = { type: "spring", stiffness: 360, damping: 28, mass: 0.6 };
const DIGIT_WIDTH = (char) => {
  if (char === "," || char === ".") return "0.28em";
  if (char === "$" || char === " ") return "0.42em";

  return "0.62em";
};

const AnimatedDigit = ({ char, reduced }) => {
  if (reduced) return <span>{char}</span>;

  return (
    <span
      className="relative inline-block overflow-hidden tabular-nums"
      style={{ minWidth: DIGIT_WIDTH(char), height: "1.05em" }}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          className="absolute inset-0 flex items-center justify-center"
          exit={{ y: "-90%", opacity: 0, filter: "blur(6px)" }}
          initial={{ y: "90%", opacity: 0, filter: "blur(6px)" }}
          key={char}
          transition={DIGIT_SPRING}
        >
          {char}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export const AnimatedNumber = ({ value, format }) => {
  const reduced = useReducedMotion();
  const display = format ? format(value) : String(value ?? "");

  return (
    <span className="inline-flex items-baseline">
      {display.split("").map((char, index) => (
        <AnimatedDigit char={char} key={index} reduced={reduced} />
      ))}
    </span>
  );
};

// Sweeping highlight skeleton — pass `className` for size + shape (rounded-md, w-20, etc).
export const Shimmer = ({ className = "" }) => {
  const reduced = useReducedMotion();

  return (
    <div
      className={`relative overflow-hidden bg-gray-200/60 dark:bg-white/[0.06] ${className}`}
    >
      {!reduced && (
        <motion.div
          animate={{ x: ["-100%", "100%"] }}
          className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-transparent via-white/50 dark:via-white/[0.08] to-transparent"
          transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      )}
    </div>
  );
};

// Three sequenced dots — quieter alternative to a spinner.
export const LoadingDots = () => {
  const reduced = useReducedMotion();

  if (reduced) return <span className="opacity-60">…</span>;

  return (
    <span className="inline-flex items-end gap-[3px] align-middle">
      {[0, 1, 2].map((i) => (
        <motion.span
          animate={{ y: [0, -4, 0], opacity: [0.35, 1, 0.35] }}
          className="inline-block w-[6px] h-[6px] rounded-full bg-current"
          key={i}
          transition={{
            duration: 0.9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.12,
          }}
        />
      ))}
    </span>
  );
};

// Staggered list-item entrance — fade + y + blur.
export const ListItemMotion = ({ index = 0, className, children, ...rest }) => {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      className={className}
      initial={{ opacity: 0, y: 6, filter: "blur(4px)" }}
      transition={{
        duration: 0.28,
        delay: Math.min(index * 0.04, 0.4),
        ease: LIST_EASE,
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const formatCurrency = (n) => `$${Number(n || 0).toLocaleString()}`;
